import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { BannerProvider, StatusBanner } from '@xingternal/banner';
import { Button } from '@xingternal/button';
import { useExperiment } from '@xingternal/hub';
import { mediaConfined } from '@xingternal/layout-tokens';
import { spaceS } from '@xingternal/tokens';

import { BookmarkAction } from '@/components/posting-header-actions/components/bookmark-action';
import { useOneClickApplyButtonsState } from '@/contexts';

const ActionsContainer = styled.div<{ $isSticky: boolean }>`
  display: flex;
  flex-direction: ${({ $isSticky }) => ($isSticky ? 'row' : 'column')};
  gap: ${spaceS};
  width: 100%;

  @media ${mediaConfined} {
    flex-direction: row;
  }
`;

const ApplyButton = styled(Button)`
  width: 100%;
`;

const PrimaryButtonWrapper = styled.div`
  flex: 1;
`;

const SecondaryActionsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${spaceS};
  flex: 1;
`;

type Props = {
  isSticky: boolean;
};

export const OneClickApplyActions: React.FC<Props> = ({ isSticky }) => {
  const experimentVariant = useExperiment('ABACUS-411');
  const {
    isOneClickApplyInProgress,
    isRegularApplyInProgress,
    oneClickApplyButtonHandler,
    regularInstantApplyButtonHandler,
  } = useOneClickApplyButtonsState();

  return (
    <BannerProvider Component={StatusBanner} defaultProps={{ display: 'fixed', variant: 'error' } as const}>
      <ActionsContainer $isSticky={isSticky}>
        {isSticky ? (
          <>
            {/* Primary button is always shown on sticky header except when secondary one is in progress*/}
            {!isRegularApplyInProgress ? (
              <ApplyButton
                variant="primary"
                onClick={oneClickApplyButtonHandler}
                loading={isOneClickApplyInProgress}
                disabled={isRegularApplyInProgress}
              >
                <FormattedMessage
                  id={`JOBS_ONE_CLICK_APPLY_PRIMARY_CTA_VARIANT_${experimentVariant === 'B' ? 'B' : 'C'}`}
                />
              </ApplyButton>
            ) : null}
            {/* Secondary button is only shown on sticky header when its action is in progress*/}
            {isRegularApplyInProgress ? (
              <ApplyButton
                variant="secondary"
                onClick={regularInstantApplyButtonHandler}
                loading={isRegularApplyInProgress}
                disabled={isOneClickApplyInProgress}
              >
                <FormattedMessage id="JOBS_ONE_CLICK_APPLY_SECONDARY_CTA" />
              </ApplyButton>
            ) : null}
            <BookmarkAction isSticky={isSticky} isIconButton={true} />
          </>
        ) : (
          <>
            <PrimaryButtonWrapper>
              <ApplyButton
                variant="primary"
                onClick={oneClickApplyButtonHandler}
                loading={isOneClickApplyInProgress}
                disabled={isRegularApplyInProgress}
              >
                <FormattedMessage
                  id={`JOBS_ONE_CLICK_APPLY_PRIMARY_CTA_VARIANT_${experimentVariant === 'B' ? 'B' : 'C'}`}
                />
              </ApplyButton>
            </PrimaryButtonWrapper>
            <SecondaryActionsWrapper>
              <ApplyButton
                variant="secondary"
                onClick={regularInstantApplyButtonHandler}
                loading={isRegularApplyInProgress}
                disabled={isOneClickApplyInProgress}
              >
                <FormattedMessage id="JOBS_ONE_CLICK_APPLY_SECONDARY_CTA" />
              </ApplyButton>
              <BookmarkAction isSticky={isSticky} isIconButton={true} />
            </SecondaryActionsWrapper>
          </>
        )}
      </ActionsContainer>
    </BannerProvider>
  );
};
